import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Snowflake } from "lucide-react";
import DemoNotice from "@/components/site/DemoNotice";

export default function Placeholder({
  title,
  subtitle,
  ctaLabel = "Back to Home",
  ctaHref = "/",
  secondaryLabel = "Request Service",
  secondaryHref = "/request-service",
}) {
  return (
    <section
      data-testid="placeholder-page"
      aria-labelledby="placeholder-heading"
      className="relative overflow-hidden"
    >
      <div
        className="absolute inset-0 bg-brand-gradient-soft"
        aria-hidden="true"
      />
      <div
        className="absolute inset-x-0 top-0 h-px bg-sun-stripe opacity-60"
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 opacity-[0.7]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 12% 20%, rgba(34,211,238,0.18) 0%, transparent 50%), radial-gradient(circle at 88% 86%, rgba(245,158,11,0.10) 0%, transparent 55%)",
        }}
        aria-hidden="true"
      />

      <div className="container-page relative flex min-h-[70vh] flex-col items-center justify-center py-20 text-center sm:py-28">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="flex flex-col items-center"
        >
          <span className="relative inline-flex h-14 w-14 items-center justify-center rounded-full bg-white shadow-soft">
            <Snowflake className="h-5 w-5 text-aqua-deep" />
            <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full bg-sun" />
          </span>

          <span className="mt-6 inline-flex items-center gap-2 rounded-full border border-aqua/30 bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-aqua-deep backdrop-blur">
            Coming in Phase 2
          </span>

          <h1
            id="placeholder-heading"
            className="mt-5 max-w-3xl font-display text-4xl font-bold leading-[1.06] tracking-tight text-navy sm:text-5xl lg:text-6xl"
          >
            {title}
          </h1>
          {subtitle && (
            <p className="mt-5 max-w-2xl text-base text-navy/70 sm:text-lg">
              {subtitle}
            </p>
          )}
          <p className="mt-4 max-w-xl text-sm text-navy/55">
            Full content coming soon &mdash; this is a multi-phase build.
          </p>

          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row">
            <Link
              to={secondaryHref}
              data-testid="placeholder-cta-primary"
              className="group inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white shadow-soft transition-colors hover:bg-navy-soft"
            >
              {secondaryLabel}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link
              to={ctaHref}
              data-testid="placeholder-cta-secondary"
              className="inline-flex items-center gap-2 rounded-full border border-navy/15 bg-white px-6 py-3 text-sm font-semibold text-navy shadow-soft transition-colors hover:bg-mist"
            >
              {ctaLabel}
            </Link>
          </div>

          <div className="mt-8">
            <DemoNotice variant="dark" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
